// ArchIE headless presentational components (fn-69.3, R2/R4/R5).
//
// PURE, side-effect-free render functions over the run-machine state.
// They own NO transport, NO tRPC — the page (`index.tsx`) wires the hook
// and hands these props. Kept separate so they unit-test under happy-dom
// without a Provider tree.
//
// Security/robustness contract (R2): every model-authored string
// (`title`/`detail`, the matter draft fields) renders as TEXT via JSX
// children — React escapes it. We NEVER `dangerouslySetInnerHTML`, never
// render markdown, never surface raw JSON.

import { useState } from 'react';

import type { HeadlessStatus } from '../../headless/index.js';
import {
  type GateView,
  type RunResult,
  type RunState,
  type ToolActivity,
  deriveMatterFields,
} from './run-machine.js';

// ── Shimmer status strip (R2) ────────────────────────────────────────

/**
 * The single `aria-live="polite"` status region. Renders the current
 * `title` as a shimmering line + an optional `detail`. A run that hasn't
 * started shows nothing. Skeleton bars (decreasing width) read as
 * "working", never a spinner.
 */
export function ShimmerStatus(props: {
  readonly status: HeadlessStatus;
  readonly isFallback: boolean;
  readonly active: boolean;
}): React.ReactElement {
  const { status, isFallback, active } = props;
  return (
    <div className="archie-shimmer" aria-live="polite" aria-busy={active}>
      <div className={`archie-shimmer-line${active ? ' is-active' : ''}`}>
        <span className="archie-shimmer-phase" data-fallback={isFallback ? 'true' : undefined}>
          {status.phase}
        </span>
        <span className="archie-shimmer-title">{status.title}</span>
      </div>
      {typeof status.detail === 'string' && status.detail.trim().length > 0 ? (
        <p className="archie-shimmer-detail">{status.detail}</p>
      ) : null}
      {active ? (
        <div className="archie-skeleton" aria-hidden="true">
          <span style={{ width: '92%' }} />
          <span style={{ width: '74%' }} />
          <span style={{ width: '58%' }} />
        </div>
      ) : null}
    </div>
  );
}

/** Subtle completed-phase scrollback + tool activity. */
export function ActivityLog(props: {
  readonly history: readonly HeadlessStatus[];
  readonly tools: readonly ToolActivity[];
}): React.ReactElement | null {
  const { history, tools } = props;
  if (history.length === 0 && tools.length === 0) {
    return null;
  }
  return (
    <details className="archie-activity">
      <summary>Activity ({history.length + tools.length})</summary>
      <ul className="archie-activity-list">
        {history.map((s, i) => (
          <li key={`h-${i}-${s.phase}`} className="archie-activity-status">
            <span className="archie-activity-phase">{s.phase}</span> {s.title}
          </li>
        ))}
        {tools.map((t, i) => (
          <li key={`t-${i}-${t.tool}`} className="archie-activity-tool">
            <span className="archie-activity-toolkind">{t.kind}</span> {t.tool}
          </li>
        ))}
      </ul>
    </details>
  );
}

// ── Restricted-form card (R4) ────────────────────────────────────────

/**
 * The attended-mode yes/no card. `review_matter` renders the matter
 * draft (with editable name + description); `create_contact`/
 * `create_matter` render a concise approve/reject. Confirm hands back the
 * (possibly edited) matter override for `review_matter`.
 */
export function RestrictedFormCard(props: {
  readonly gate: GateView;
  readonly busy: boolean;
  readonly onConfirm: (matterOverride?: Record<string, unknown>) => void;
  readonly onReject: () => void;
}): React.ReactElement {
  const { gate, busy, onConfirm, onReject } = props;
  const isReviewMatter = gate.bareName === 'review_matter';

  if (isReviewMatter) {
    return <ReviewMatterCard gate={gate} busy={busy} onConfirm={onConfirm} onReject={onReject} />;
  }

  const label = gate.bareName === 'create_matter' ? 'Create matter' : 'Create contact';
  const fields = deriveMatterFields(gate.toolArgs);
  return (
    <section className="archie-card" aria-label={`Approve ${label}`}>
      <header className="archie-card-head">
        <h2>{label}</h2>
        <code className="archie-card-tool">{gate.toolName}</code>
      </header>
      <dl className="archie-card-fields">
        {fields.name !== undefined ? <Field label="Name" value={fields.name} /> : null}
        {fields.client !== undefined ? <Field label="Client" value={fields.client} /> : null}
        {fields.otherSide !== undefined ? (
          <Field label="Other side" value={fields.otherSide} />
        ) : null}
        {fields.extra.map(([k, v]) => (
          <Field key={k} label={k} value={v} />
        ))}
      </dl>
      <CardActions
        busy={busy}
        onConfirm={() => onConfirm()}
        onReject={onReject}
        confirm="Approve"
      />
    </section>
  );
}

function ReviewMatterCard(props: {
  readonly gate: GateView;
  readonly busy: boolean;
  readonly onConfirm: (matterOverride?: Record<string, unknown>) => void;
  readonly onReject: () => void;
}): React.ReactElement {
  const { gate, busy, onConfirm, onReject } = props;
  const fields = deriveMatterFields(gate.toolArgs);
  const [name, setName] = useState(fields.name ?? '');
  const [description, setDescription] = useState(fields.description ?? '');

  const confirm = (): void => {
    // Merge edits over the original draft so unknown fields survive. The two
    // editable fields propagate the human's value AUTHORITATIVELY — including
    // an explicit CLEAR: blanking the description drops it from the draft
    // (the prior "only set when non-empty" kept the stale original). Drop the
    // original `description` first (via destructure, not `delete`), then
    // re-add only when the human left a non-empty value.
    const { description: _priorDescription, ...rest } = gate.toolArgs;
    const override: Record<string, unknown> = { ...rest, name: name.trim() };
    const trimmedDescription = description.trim();
    if (trimmedDescription.length > 0) {
      override.description = trimmedDescription;
    }
    onConfirm(override);
  };

  return (
    <section className="archie-card" aria-label="Review matter">
      <header className="archie-card-head">
        <h2>Review matter</h2>
        <code className="archie-card-tool">{gate.toolName}</code>
      </header>
      <p className="archie-card-hint">
        Confirm the matter the agent assembled. You can adjust the name and description before it
        proceeds to create the contact and matter.
      </p>
      <label className="archie-field-edit">
        <span>Matter name</span>
        <input
          type="text"
          value={name}
          disabled={busy}
          onChange={(e) => setName(e.target.value)}
          placeholder="Matter name"
        />
      </label>
      <dl className="archie-card-fields">
        {fields.matterType !== undefined ? (
          <Field label="Matter type" value={fields.matterType} />
        ) : null}
        {fields.client !== undefined ? <Field label="Client" value={fields.client} /> : null}
        {fields.otherSide !== undefined ? (
          <Field label="Other side" value={fields.otherSide} />
        ) : null}
        {fields.extra.map(([k, v]) => (
          <Field key={k} label={k} value={v} />
        ))}
      </dl>
      <label className="archie-field-edit">
        <span>Description</span>
        <textarea
          value={description}
          disabled={busy}
          rows={3}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Matter description"
        />
      </label>
      <CardActions busy={busy} onConfirm={confirm} onReject={onReject} confirm="Confirm matter" />
    </section>
  );
}

function Field(props: { readonly label: string; readonly value: string }): React.ReactElement {
  return (
    <div className="archie-field">
      <dt>{props.label}</dt>
      <dd>{props.value}</dd>
    </div>
  );
}

function CardActions(props: {
  readonly busy: boolean;
  readonly confirm: string;
  readonly onConfirm: () => void;
  readonly onReject: () => void;
}): React.ReactElement {
  return (
    <div className="archie-card-actions">
      <button
        type="button"
        className="archie-btn archie-btn-primary"
        disabled={props.busy}
        onClick={props.onConfirm}
      >
        {props.confirm}
      </button>
      <button
        type="button"
        className="archie-btn archie-btn-ghost"
        disabled={props.busy}
        onClick={props.onReject}
      >
        Reject
      </button>
    </div>
  );
}

// ── Final result (R5) ────────────────────────────────────────────────

export function ResultPanel(props: { readonly result: RunResult }): React.ReactElement {
  const { result } = props;
  if (result.kind === 'created') {
    const fields = result.matter !== undefined ? deriveMatterFields(result.matter) : undefined;
    return (
      <section className="archie-result archie-result-ok">
        <h2>Matter created</h2>
        {fields !== undefined ? (
          <dl className="archie-card-fields">
            {fields.id !== undefined ? <Field label="Matter ID" value={fields.id} /> : null}
            {fields.name !== undefined ? <Field label="Name" value={fields.name} /> : null}
            {fields.matterType !== undefined ? (
              <Field label="Type" value={fields.matterType} />
            ) : null}
            {fields.client !== undefined ? <Field label="Client" value={fields.client} /> : null}
            {fields.otherSide !== undefined ? (
              <Field label="Other side" value={fields.otherSide} />
            ) : null}
          </dl>
        ) : (
          <p>The platform flow completed.</p>
        )}
        {fields !== undefined && fields.id === undefined ? (
          <p className="archie-result-reason">
            Showing the confirmed matter draft; the backend did not return a matter id on this run.
          </p>
        ) : null}
      </section>
    );
  }
  if (result.kind === 'rejected') {
    return (
      <section className="archie-result archie-result-warn">
        <h2>Intake stopped</h2>
        <p>
          You rejected the <strong>{result.tool}</strong> step. Nothing further was created.
        </p>
      </section>
    );
  }
  return (
    <section className="archie-result archie-result-warn">
      <h2>Smokeball backend unavailable</h2>
      <p>
        The platform flow completed through <strong>{result.lastPhase}</strong>, but the matter
        could not be confirmed end-to-end.
      </p>
      <p className="archie-result-reason">{result.reason}</p>
    </section>
  );
}

// ── Intake form (R5) ─────────────────────────────────────────────────

export interface IntakeSubmit {
  readonly mode: RunState['mode'];
  readonly agentId: string;
  readonly documentText: string;
  readonly fileId?: string;
}

/**
 * The pre-run intake: agent id, mode toggle, a file picker (upload →
 * `file_id`, mounted at create) AND a paste-text fallback. Start is
 * enabled once there is document text OR an uploaded file.
 */
export function IntakeForm(props: {
  readonly defaultAgentId: string;
  readonly uploading: boolean;
  readonly uploadError?: string;
  readonly uploadedFile?: { readonly id: string; readonly name: string };
  readonly onPickFile: (file: File) => void;
  readonly onStart: (input: IntakeSubmit) => void;
  readonly disabled: boolean;
}): React.ReactElement {
  const [agentId, setAgentId] = useState(props.defaultAgentId);
  const [mode, setMode] = useState<RunState['mode']>('attended');
  const [documentText, setDocumentText] = useState('');

  const canStart =
    !props.disabled &&
    agentId.trim().length > 0 &&
    (documentText.trim().length > 0 || props.uploadedFile !== undefined);

  const submit = (): void => {
    if (!canStart) {
      return;
    }
    props.onStart({
      mode,
      agentId: agentId.trim(),
      documentText: documentText.trim(),
      ...(props.uploadedFile !== undefined ? { fileId: props.uploadedFile.id } : {}),
    });
  };

  return (
    <section className="archie-intake">
      <div className="archie-field-edit">
        <span>Agent ID</span>
        <input
          type="text"
          value={agentId}
          disabled={props.disabled}
          onChange={(e) => setAgentId(e.target.value)}
          spellCheck={false}
        />
      </div>

      <fieldset className="archie-mode" disabled={props.disabled}>
        <legend>Mode</legend>
        <label>
          <input
            type="radio"
            name="archie-mode"
            checked={mode === 'attended'}
            onChange={() => setMode('attended')}
          />
          Attended — show a yes/no card at each restricted form
        </label>
        <label>
          <input
            type="radio"
            name="archie-mode"
            checked={mode === 'lights-out'}
            onChange={() => setMode('lights-out')}
          />
          Lights-out — auto-approve every restricted form
        </label>
      </fieldset>

      <div className="archie-field-edit">
        <span>Document</span>
        <label className="archie-upload">
          <input
            type="file"
            aria-label="Upload and mount a document"
            disabled={props.disabled || props.uploading}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) {
                props.onPickFile(f);
              }
            }}
          />
          <span className="archie-chip">
            {props.uploading
              ? 'Uploading…'
              : props.uploadedFile !== undefined
                ? `Mounted: ${props.uploadedFile.name}`
                : 'Upload + mount a document'}
          </span>
        </label>
        {props.uploadError !== undefined ? (
          <p className="archie-error" role="alert">
            {props.uploadError}
          </p>
        ) : null}
      </div>

      <label className="archie-field-edit">
        <span>…or paste the document text (reliable fallback)</span>
        <textarea
          value={documentText}
          disabled={props.disabled}
          rows={8}
          placeholder="Paste the intake document text here. The agent reads this inline."
          onChange={(e) => setDocumentText(e.target.value)}
        />
      </label>

      <p className="archie-intake-warn">
        Do not paste secrets — document text persists in agent event history.
      </p>

      <button
        type="button"
        className="archie-btn archie-btn-primary archie-start"
        disabled={!canStart}
        onClick={submit}
      >
        Start intake
      </button>
    </section>
  );
}
